import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Brush } from 'recharts';
import api from '../../services/api';
import './DriftMonitoringApp.css';

// Observed-leg options: default floating (best coverage; some platforms don't report the others).
const LEGS = [
  ['floating', 'Floating Target %'],
  ['last_updated', 'Last Updated Target %'],
  ['shadow_client', 'Shadow Client Portfolio %'],
];
const STATUS_META = {
  DRIFT: ['Large drift', 'dm-b-drift'],
  EXECUTION_FAIL: ['Execution fail', 'dm-b-exec'],
  UNEXPECTED: ['Unexpected', 'dm-b-unexp'],
  NO_DATA: ['No data', 'dm-b-nodata'],
  OK: ['OK', 'dm-b-ok'],
};
const fmt = (v, signed = false) => {
  if (v === null || v === undefined) return '—';
  const t = Math.abs(v) < 0.005 ? '0' : v.toFixed(2);
  return signed && v > 0 ? '+' + t : t;
};
const Badge = ({ status }) => {
  const [label, cls] = STATUS_META[status] || [status, ''];
  return <span className={`dm-badge ${cls}`}>{label}</span>;
};

// ── summary tiles (also act as exception filters) ─────────────────────
const TILES = [
  ['portfolio_count', 'Portfolios assessed', 'dm-t-navy', null],
  ['breach_count', 'Large drift', 'dm-t-drift', 'DRIFT'],
  ['exec_fail_count', 'Execution failures', 'dm-t-exec', 'EXECUTION_FAIL'],
  ['unexpected_count', 'Unexpected holdings', 'dm-t-unexp', 'UNEXPECTED'],
  ['no_data_count', 'No data (this leg)', 'dm-t-nodata', 'NO_DATA'],
  ['not_reported_count', 'Model, no holdings', 'dm-t-grey', null],
  ['no_model_count', 'No approved model', 'dm-t-grey', null],
  ['stale_count', 'Stale / unconfirmed', 'dm-t-stale', null],
];

// ── Portfolio accordion row (shared by the Portfolios and Suite view tabs) ──
const exceptionCount = (p) => p.breach_count + p.exec_fail_count + p.unexpected_count;

const StaleToggle = ({ checked, onChange, count }) => (
  <label className="dm-muted" style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#cdd7e1' }}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    Stale / unconfirmed only ({count || 0})
  </label>
);

function PortfolioRow({ p, open, onToggle, onDrift }) {
  const breaches = exceptionCount(p);
  return (
    <div id={`dm-pf-${p.portfolio_code}`} className="dm-pf">
      <button className="dm-pf-head" onClick={() => onToggle(p.portfolio_code)}>
        <span className="dm-pf-caret">{open ? '▾' : '▸'}</span>
        <span className="dm-pf-name">{p.portfolio_name}</span>
        <span className="dm-pf-meta">
          <span className="dm-pf-code">{p.portfolio_code}</span>
          {' · master '}{p.blender_code || 'unmapped'}
          {p.platform ? ' · ' + p.platform : ''}
        </span>
        <span className="dm-pf-stats">
          {p.stale && <span className="dm-pf-stale" title={p.stale_reason || 'Potentially stale data'}>STALE</span>}
          <span className="dm-pf-asat" title={`Last Shadow ${p.last_shadow_date || '?'}` + (p.last_confirmed_date ? ` · last confirmed position ${p.last_confirmed_date}` : ' · no confirmed position')}>
            as at {p.last_shadow_date || 'n/a'}</span>
          <span title="Total absolute drift">Σ|drift| {fmt(p.total_abs_drift)}%</span>
          {breaches > 0 && <span className="dm-pf-breaches">{breaches} exception{breaches === 1 ? '' : 's'}</span>}
          <span className={`dm-cov ${p.coverage_pct < 50 ? 'dm-cov-low' : ''}`} title="Share of the portfolio the selected leg accounts for">
            {fmt(p.coverage_pct)}% cov</span>
        </span>
      </button>
      {open && (
        <>
        <div className="dm-pf-sub">
          {p.growth_defensive && (
            <span className="dm-gd" title={`Model ${fmt(p.growth_defensive.approved_growth)} / ${fmt(p.growth_defensive.approved_defensive)} · observed ${fmt(p.growth_defensive.observed_growth)} / ${fmt(p.growth_defensive.observed_defensive)}`
                                          + (p.growth_defensive.observed_unclassified ? ` · unclassified ${fmt(p.growth_defensive.observed_unclassified)}` : '')}>
              Growth/Defensive: {fmt(p.growth_defensive.observed_growth)} / {fmt(p.growth_defensive.observed_defensive)}
              <span className="dm-muted"> vs model {fmt(p.growth_defensive.approved_growth)} / {fmt(p.growth_defensive.approved_defensive)}</span>
              <span className={`dm-tiltchip ${p.growth_defensive.growth_drift >= 0 ? 'dm-over' : 'dm-under'}`}>
                growth {fmt(p.growth_defensive.growth_drift, true)}%</span>
            </span>
          )}
          <span className="dm-muted">Net tilt:</span>
          {(p.acg_tilt || []).slice(0, 6).map((t) => (
            <span key={t.asset_class_group} className={`dm-tiltchip ${t.net_drift >= 0 ? 'dm-over' : 'dm-under'}`}>
              {t.asset_class_group} {fmt(t.net_drift, true)}%
            </span>
          ))}
          <button className="dm-btn-link" onClick={() => onDrift(p.portfolio_code, p.blender_code)}>Drift over time →</button>
        </div>
        <div className="dm-table-scroll">
          <table className="dm-table dm-pf-table">
            <thead><tr>
              <th>Sub Asset Class / Fund</th><th className="dm-r">Approved</th><th className="dm-r">Last Upd.</th>
              <th className="dm-r">Floating</th><th className="dm-r">Client</th><th className="dm-r">Observed</th>
              <th className="dm-r">Drift</th><th>Status</th>
            </tr></thead>
            <tbody>
              {p.sub_asset_classes.map((sac) => (
                <React.Fragment key={sac.sub_asset_class}>
                  <tr className="dm-sac">
                    <td>{sac.sub_asset_class}</td>
                    <td className="dm-r">{fmt(sac.approved_total)}</td><td /><td /><td />
                    <td className="dm-r">{fmt(sac.observed_total)}</td>
                    <td className="dm-r dm-drift">{fmt(sac.drift, true)}</td><td />
                  </tr>
                  {sac.funds.map((f, j) => (
                    <tr key={j} className={f.status === 'OK' ? '' : `dm-row-${f.severity || 'na'}`}>
                      <td className="dm-fund">{f.fund_name}
                        {f.apir_code && <span className="dm-code-inline">{f.apir_code}</span>}</td>
                      <td className="dm-r">{fmt(f.approved_target)}</td>
                      <td className="dm-r">{fmt(f.last_updated)}</td>
                      <td className="dm-r">{fmt(f.floating)}</td>
                      <td className="dm-r">{fmt(f.shadow_client)}</td>
                      <td className="dm-r">{fmt(f.observed)}</td>
                      <td className="dm-r dm-drift">{fmt(f.drift, true)}</td>
                      <td><Badge status={f.status} /></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

// ── Portfolios tab: worst drift first, optionally narrowed to one portfolio suite so
// whoever looks after a suite only sees its portfolios. Suite membership and the
// dropdown order come from /masters (trading Cascade order: flagship suite first);
// portfolios whose master isn't in the cascade fall to a trailing group.
const NO_SUITE = 'Unmapped / not in cascade';
const SUITE_KEY = 'dm.portfolios.suite';

function PortfoliosView({ data, suiteOf, suiteOrder, suitesErr, suite, setSuite,
                          staleOnly, setStaleOnly, expanded, onToggle, onDrift }) {
  const pool = useMemo(() => data.portfolios.filter((p) => !staleOnly || p.stale), [data, staleOnly]);
  const counts = useMemo(() => {
    const c = {};
    if (!suiteOf) return c;
    pool.forEach((p) => {
      const s = suiteOf[p.blender_code] || NO_SUITE;
      c[s] = c[s] || { n: 0, exc: 0 };
      c[s].n += 1; c[s].exc += exceptionCount(p);
    });
    return c;
  }, [pool, suiteOf]);
  const options = [...(suiteOrder || []), NO_SUITE].filter((s) => counts[s]);
  const active = suite && counts[suite] ? suite : '';
  const shown = active ? pool.filter((p) => (suiteOf[p.blender_code] || NO_SUITE) === active) : pool;

  return (
    <div className="dm-section">
      <div className="dm-section-head">
        <h2>Portfolios <span className="dm-muted">· {active ? `${shown.length} in ${active}` : `${data.portfolios.length} assessed`}, worst drift first</span></h2>
        <div className="dm-filters">
          <label className="dm-suite-pick">Suite
            <select value={active} onChange={(e) => setSuite(e.target.value)} disabled={!suiteOf}>
              <option value="">{suiteOf ? 'All suites' : (suitesErr ? 'Suites unavailable' : 'Loading suites…')}</option>
              {options.map((s) => (
                <option key={s} value={s}>{s} ({counts[s].n}{counts[s].exc ? ` · ${counts[s].exc} exc` : ''})</option>
              ))}
            </select>
          </label>
          <StaleToggle checked={staleOnly} onChange={setStaleOnly} count={data.summary.stale_count} />
        </div>
      </div>
      {suitesErr && <div className="dm-alert" style={{ margin: 10 }}>Suites failed to load: {suitesErr}</div>}
      {suite && suiteOf && !active && (
        <div className="dm-note" style={{ margin: 10 }}>
          No portfolios in <strong>{suite}</strong> for the current filters, so all suites are shown.
        </div>
      )}
      <div className="dm-pf-list">
        {shown.map((p) => (
          <PortfolioRow key={p.portfolio_code} p={p} open={expanded[p.portfolio_code]}
                        onToggle={onToggle} onDrift={onDrift} />
        ))}
      </div>
    </div>
  );
}

// ── Master matrix: vehicles sharing a master (blender_code) as columns, funds as
// rows. Built entirely from the /report payload (no extra backend call). Cells show
// drift or observed weight, coloured by status, so a fund that has drifted in one
// vehicle but not its siblings stands out across the row.
const CELL_BG = { OK: '#eaf6ec', DRIFT: '#fff3cd', EXECUTION_FAIL: '#f8d7da', UNEXPECTED: '#fce5cd', NO_DATA: '#e7e6e6' };

function MasterMatrix({ data }) {
  const byMaster = useMemo(() => {
    const m = {};
    (data.portfolios || []).forEach((p) => { const k = p.blender_code || 'unmapped'; (m[k] = m[k] || []).push(p); });
    return m;
  }, [data]);
  const masters = useMemo(
    () => Object.keys(byMaster).sort((a, b) => byMaster[b].length - byMaster[a].length || a.localeCompare(b)),
    [byMaster]);
  const [master, setMaster] = useState('');
  const [cellMode, setCellMode] = useState('drift');
  useEffect(() => { if (masters.length && !byMaster[master]) setMaster(masters[0]); }, [masters]); // eslint-disable-line react-hooks/exhaustive-deps

  const vehicles = byMaster[master] || [];
  const rows = useMemo(() => {
    const fundKey = (f) => f.apir_code || f.fund_name;
    const rowMap = {}, cells = {};
    vehicles.forEach((p) => (p.sub_asset_classes || []).forEach((sac) => sac.funds.forEach((f) => {
      const k = fundKey(f);
      if (!rowMap[k]) rowMap[k] = { key: k, sub: f.sub_asset_class, name: f.fund_name, apir: f.apir_code,
                                    approved: f.approved_target, ge: f.growth_exposure };
      else if (rowMap[k].approved == null && f.approved_target != null) rowMap[k].approved = f.approved_target;
      (cells[k] = cells[k] || {})[p.portfolio_code] = f;
    })));
    return Object.values(rowMap).map((r) => ({ ...r, cells: cells[r.key] }))
      .sort((a, b) => (a.sub || '').localeCompare(b.sub || '') || (a.name || '').localeCompare(b.name || ''));
  }, [master, vehicles]);

  // Growth / defensive totals. Model column from the matrix's own Model weights × each
  // SAC's growth exposure; vehicle columns from the builder's per-portfolio split.
  // A SAC missing from the taxonomy (ge null) shows as its own Unclassified line.
  const gdLines = useMemo(() => {
    const model = { growth: 0, defensive: 0, unclassified: 0 };
    rows.forEach((r) => {
      const w = r.approved || 0;
      if (r.ge == null) model.unclassified += w;
      else { model.growth += w * r.ge; model.defensive += w * (1 - r.ge); }
    });
    const keys = [['Growth', 'growth'], ['Defensive', 'defensive']];
    const anyUnc = model.unclassified > 0.005 || vehicles.some((p) => {
      const g = p.growth_defensive || {};
      return Math.abs(g.observed_unclassified || 0) > 0.005 || Math.abs(g.approved_unclassified || 0) > 0.005;
    });
    if (anyUnc) keys.push(['Unclassified', 'unclassified']);
    return keys.map(([label, k]) => ({ label, k, model: model[k] }));
  }, [rows, vehicles]);

  if (!masters.length) return null;

  return (
    <div className="dm-section">
      <div className="dm-section-head">
        <h2>Master matrix <span className="dm-muted">· funds × vehicles sharing a master</span></h2>
        <div className="dm-filters">
          <select value={master} onChange={(e) => setMaster(e.target.value)}>
            {masters.map((m) => <option key={m} value={m}>{m} ({byMaster[m].length})</option>)}
          </select>
          <select value={cellMode} onChange={(e) => setCellMode(e.target.value)}>
            <option value="drift">Cells: Drift</option>
            <option value="observed">Cells: Observed</option>
          </select>
        </div>
      </div>
      <div className="dm-table-scroll">
        <table className="dm-table dm-matrix">
          <thead><tr>
            <th className="dm-mx-fund">Sub AC / Fund</th>
            <th className="dm-r">Model</th>
            {vehicles.map((p) => (
              <th key={p.portfolio_code} className="dm-r dm-mx-vh"
                  title={`${p.portfolio_name} (${p.portfolio_code})${p.stale ? ' [STALE]' : ''}`}>
                {p.portfolio_code}{p.stale ? ' *' : ''}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td className="dm-mx-fund"><span className="dm-mx-sac">{r.sub}</span> {r.name}
                  {r.apir && <span className="dm-code-inline">{r.apir}</span>}</td>
                <td className="dm-r">{fmt(r.approved)}</td>
                {vehicles.map((p) => {
                  const f = r.cells[p.portfolio_code];
                  if (!f) return <td key={p.portfolio_code} className="dm-r dm-mx-empty" />;
                  const val = cellMode === 'drift' ? f.drift : f.observed;
                  return (
                    <td key={p.portfolio_code} className="dm-r" style={{ background: CELL_BG[f.status] || '#fff' }}
                        title={`${p.portfolio_name}: approved ${fmt(f.approved_target)} · observed ${fmt(f.observed)} · drift ${fmt(f.drift, true)} · ${f.status}`}>
                      {cellMode === 'drift' ? fmt(val, true) : fmt(val)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={vehicles.length + 2} className="dm-muted dm-empty">No funds for this master.</td></tr>}
            {rows.length > 0 && gdLines.map(({ label, k, model }) => (
              <tr key={k} className="dm-mx-total">
                <td className="dm-mx-fund">{label} %</td>
                <td className="dm-r">{fmt(model)}</td>
                {vehicles.map((p) => {
                  const g = p.growth_defensive || {};
                  const obs = g[`observed_${k}`], ap = g[`approved_${k}`];
                  const drift = obs != null && ap != null ? obs - ap : null;
                  return (
                    <td key={p.portfolio_code} className="dm-r"
                        title={`${p.portfolio_name}: model ${fmt(ap)} · observed ${fmt(obs)} · drift ${fmt(drift, true)}`}>
                      {cellMode === 'drift' ? fmt(drift, true) : fmt(obs)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="dm-muted dm-pad">Vehicles marked * have stale / unconfirmed data. Cell colour = status. Model = the approved target (can differ per vehicle where overrides apply).</div>
    </div>
  );
}

// ── Drift over time: gross |floating − target| per period for a master (avg across
// its vehicles) or a single vehicle. Drag the brush to select a period; the table
// ranks the funds driving the diff over that window. Masters are cascade-ordered.
function DriftOverTime({ scope }) {
  const [masters, setMasters] = useState(null);
  const [bc, setBc] = useState('');
  const [pc, setPc] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState(null);
  const [modalIdx, setModalIdx] = useState(null);   // clicked date -> per-fund diff modal
  const pendingPc = useRef(null);                    // vehicle to land on after a click-through

  useEffect(() => {
    api.get('/api/drift-monitoring/masters')
      .then((r) => { const m = r.data.masters || []; setMasters(m); if (m[0] && !bc) setBc(m[0].blender_code); })
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // click-through from another tab: jump to a specific master + vehicle
  useEffect(() => {
    if (scope?.bc) { pendingPc.current = scope.pc || ''; setBc(scope.bc); }
  }, [scope]);

  const master = useMemo(() => (masters || []).find((m) => m.blender_code === bc), [masters, bc]);
  // default to the actual master portfolio (not the vehicle average) unless a
  // click-through requested a specific vehicle.
  useEffect(() => {
    const m = (masters || []).find((x) => x.blender_code === bc);
    if (pendingPc.current != null) { setPc(pendingPc.current); pendingPc.current = null; }
    else setPc(m?.master_code || '');
  }, [bc, masters]);

  useEffect(() => {
    if (!bc) return;
    setData(null); setRange(null); setModalIdx(null); setLoading(true); setErr(null);
    api.get('/api/drift-monitoring/drift-series', { params: pc ? { portfolio: pc } : { master: bc } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [bc, pc]);

  const chart = useMemo(() => (data?.dates || []).map((d, i) => ({ date: d, gross: data.gross[i] })), [data]);
  const suites = useMemo(() => {
    const g = {};
    (masters || []).forEach((m) => { (g[m.suite] = g[m.suite] || []).push(m); });
    return Object.entries(g);
  }, [masters]);

  const n = data?.dates?.length || 0;
  const i0 = range ? range[0] : 0;
  const i1 = range ? range[1] : Math.max(0, n - 1);

  const nearestIdx = (val) => {
    const t = new Date(val).getTime();
    let best = 0, bd = Infinity;
    (data?.dates || []).forEach((d, i) => { const diff = Math.abs(new Date(d).getTime() - t); if (diff < bd) { bd = diff; best = i; } });
    return best;
  };

  const breakdown = useMemo(() => {
    if (!data || !n) return [];
    return data.funds.map((f) => {
      const seg = f.drift.slice(i0, i1 + 1).filter((v) => v != null);
      const avg = seg.length ? seg.reduce((a, b) => a + Math.abs(b), 0) / seg.length : 0;
      return { apir: f.apir, name: f.name, avg_abs: avg, at_end: f.drift[i1] };
    }).filter((f) => f.avg_abs > 0.005).sort((a, b) => b.avg_abs - a.avg_abs).slice(0, 30);
  }, [data, i0, i1, n]);

  const modalFunds = useMemo(() => {
    if (modalIdx == null || !data) return [];
    return data.funds.map((f) => ({ apir: f.apir, name: f.name, drift: f.drift[modalIdx] }))
      .filter((f) => f.drift != null && Math.abs(f.drift) > 0.005)
      .sort((a, b) => Math.abs(b.drift) - Math.abs(a.drift));
  }, [modalIdx, data]);

  return (
    <div className="dm-section">
      <div className="dm-section-head">
        <h2>Drift over time <span className="dm-muted">· gross |floating − target|; click a point for that date, brush or date pickers for a period</span></h2>
        <div className="dm-filters">
          <select value={bc} onChange={(e) => setBc(e.target.value)}>
            {suites.map(([suite, ms]) => (
              <optgroup key={suite} label={suite}>
                {ms.map((m) => <option key={m.blender_code} value={m.blender_code}>{m.name} ({m.blender_code}) · {m.vehicles.length}</option>)}
              </optgroup>
            ))}
          </select>
          <select value={pc} onChange={(e) => setPc(e.target.value)}>
            <option value="">All vehicles (master avg)</option>
            {(master?.vehicles || []).map((v) => (
              <option key={v.portfolio_code} value={v.portfolio_code}>
                {v.portfolio_name} ({v.portfolio_code}){v.portfolio_code === master?.master_code ? ' · master' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>
      {err && <div className="dm-alert">{err}</div>}
      {loading && <p className="dm-muted dm-pad">Loading…</p>}
      {data && !loading && n > 0 && (
        <div style={{ padding: '8px 14px' }}>
          <div className="dm-daterange">
            <label>From <input type="date" value={data.dates[i0]} min={data.dates[0]} max={data.dates[n - 1]}
              onChange={(e) => { const i = nearestIdx(e.target.value); setRange([Math.min(i, i1), Math.max(i, i1)]); }} /></label>
            <label>To <input type="date" value={data.dates[i1]} min={data.dates[0]} max={data.dates[n - 1]}
              onChange={(e) => { const i = nearestIdx(e.target.value); setRange([Math.min(i0, i), Math.max(i0, i)]); }} /></label>
            {range && <button className="dm-btn-link" onClick={() => setRange(null)}>reset</button>}
          </div>
          {!pc && (
            <div className="dm-muted" style={{ marginBottom: 4 }}>
              Equal-weighted average across {data.vehicles} of {data.portfolio_codes.length} mapped
              vehicle{data.portfolio_codes.length === 1 ? '' : 's'} that report Shadow floating data.
            </div>
          )}
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chart} margin={{ top: 8, right: 16, left: -6, bottom: 4 }}
              onClick={(s) => { if (s && s.activeTooltipIndex != null) setModalIdx(s.activeTooltipIndex); }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={40} />
              <YAxis tick={{ fontSize: 10 }} unit="%" width={44} domain={[0, 'auto']} />
              <Tooltip formatter={(v) => [`${Number(v).toFixed(2)}%`, 'Gross drift']} />
              <Line type="monotone" dataKey="gross" stroke="#072040" strokeWidth={1.5} dot={false}
                    activeDot={{ r: 4, style: { cursor: 'pointer' } }} />
              <Brush dataKey="date" height={22} stroke="#599BE3" travellerWidth={8}
                     startIndex={i0} endIndex={i1}
                     onChange={(r) => (r && (r.startIndex !== i0 || r.endIndex !== i1) && setRange([r.startIndex, r.endIndex]))} />
            </LineChart>
          </ResponsiveContainer>
          <div className="dm-section-head" style={{ marginTop: 8, borderRadius: 6 }}>
            <h2>Driving the diff <span className="dm-muted">· {data.dates[i0]} to {data.dates[i1]} · avg |drift| over the selected period</span></h2>
          </div>
          <div className="dm-table-scroll dm-mini">
            <table className="dm-table"><thead><tr>
              <th>Fund</th><th>APIR</th><th className="dm-r">Avg |drift| %</th><th className="dm-r">Drift at end %</th>
            </tr></thead>
              <tbody>{breakdown.map((f) => (
                <tr key={f.apir} className={Math.abs(f.at_end || 0) > 0.5 ? 'dm-row-medium' : ''}>
                  <td>{f.name}</td><td className="dm-code-inline">{f.apir}</td>
                  <td className="dm-r">{f.avg_abs.toFixed(2)}</td>
                  <td className="dm-r dm-drift">{fmt(f.at_end, true)}</td>
                </tr>
              ))}{!breakdown.length && <tr><td colSpan={4} className="dm-muted dm-empty">No material drift in this period.</td></tr>}</tbody>
            </table>
          </div>
          {data.truncated && <div className="dm-muted dm-empty">Showing the {data.funds.length} biggest movers.</div>}
        </div>
      )}

      {modalIdx != null && data && (
        <div className="dm-modal-overlay" onClick={() => setModalIdx(null)}>
          <div className="dm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dm-modal-head">
              <h3>Fund diffs · {data.dates[modalIdx]} <span className="dm-muted">· floating − target</span></h3>
              <button className="dm-btn-link" onClick={() => setModalIdx(null)}>✕</button>
            </div>
            <div className="dm-table-scroll" style={{ maxHeight: '64vh' }}>
              <table className="dm-table"><thead><tr><th>Fund</th><th>APIR</th><th className="dm-r">Diff %</th></tr></thead>
                <tbody>{modalFunds.map((f) => (
                  <tr key={f.apir} className={Math.abs(f.drift) > 0.5 ? 'dm-row-medium' : ''}>
                    <td>{f.name}</td><td className="dm-code-inline">{f.apir}</td>
                    <td className="dm-r dm-drift">{fmt(f.drift, true)}</td>
                  </tr>
                ))}{!modalFunds.length && <tr><td colSpan={3} className="dm-muted dm-empty">No material diffs on this date.</td></tr>}</tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Platform data-quality: reporting coverage + staleness grouped by platform,
// and the data-quality guardrail issues.
function PlatformView({ data }) {
  const rows = useMemo(() => {
    const all = [
      ...(data.portfolios || []).map((p) => ({ ...p, kind: 'assessed' })),
      ...(data.not_reported || []).map((p) => ({ ...p, kind: 'not_reported' })),
      ...(data.no_model || []).map((p) => ({ ...p, kind: 'no_model' })),
    ];
    const g = {};
    all.forEach((p) => {
      const k = p.platform || '(unmapped)';
      const r = g[k] || (g[k] = { platform: k, total: 0, assessed: 0, stale: 0, not_reported: 0, no_model: 0, breaches: 0, newest: null });
      r.total++;
      if (p.kind === 'assessed') { r.assessed++; if (p.stale) r.stale++; r.breaches += (p.breach_count || 0) + (p.exec_fail_count || 0) + (p.unexpected_count || 0); }
      if (p.kind === 'not_reported') r.not_reported++;
      if (p.kind === 'no_model') r.no_model++;
      if (p.last_shadow_date && (r.newest == null || p.last_shadow_date > r.newest)) r.newest = p.last_shadow_date;
    });
    return Object.values(g).sort((a, b) => (b.stale + b.not_reported) - (a.stale + a.not_reported) || b.total - a.total);
  }, [data]);
  const dq = data.data_quality || [];
  return (
    <>
      <div className="dm-section">
        <div className="dm-section-head"><h2>Platform data quality <span className="dm-muted">· reporting coverage and staleness by platform</span></h2></div>
        <div className="dm-table-scroll">
          <table className="dm-table"><thead><tr>
            <th>Platform</th><th className="dm-r">Portfolios</th><th className="dm-r">Assessed</th>
            <th className="dm-r">Stale</th><th className="dm-r">No holdings</th><th className="dm-r">No model</th>
            <th className="dm-r">Exceptions</th><th className="dm-r">Latest Shadow</th>
          </tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.platform} className={(r.stale + r.not_reported) > 0 ? 'dm-row-medium' : ''}>
                <td>{r.platform}</td><td className="dm-r">{r.total}</td><td className="dm-r">{r.assessed}</td>
                <td className="dm-r">{r.stale || ''}</td><td className="dm-r">{r.not_reported || ''}</td>
                <td className="dm-r">{r.no_model || ''}</td><td className="dm-r">{r.breaches || ''}</td>
                <td className="dm-r">{r.newest || 'never'}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
      <div className="dm-section">
        <div className="dm-section-head"><h2>Data quality guardrails <span className="dm-muted">· {dq.length}</span></h2></div>
        {!dq.length && <p className="dm-muted dm-pad">No data-quality issues detected (approved sets foot to 100%, all model funds carry an APIR).</p>}
        {dq.length > 0 && (
          <div className="dm-table-scroll dm-mini">
            <table className="dm-table"><thead><tr><th>Portfolio</th><th>Issue</th><th>Detail</th></tr></thead>
              <tbody>{dq.map((d, i) => (
                <tr key={i} className="dm-row-medium"><td>{d.portfolio_name}</td><td>{d.issue}</td><td>{d.detail}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ── Tolerance overrides editor (default / per sub-asset-class / per fund).
function TolerancesModal({ onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ scope_type: 'sac', scope_value: '', tolerance_pct: 1.0, note: '' });
  const load = () => api.get('/api/drift-monitoring/tolerances').then((r) => setRows(r.data.tolerances || [])).catch((e) => setErr(e.response?.data?.detail || e.message));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setErr(null);
    try {
      await api.put('/api/drift-monitoring/tolerances', { ...form, scope_value: form.scope_type === 'default' ? '' : form.scope_value, tolerance_pct: Number(form.tolerance_pct) });
      setForm({ ...form, scope_value: '', note: '' }); load(); onChanged && onChanged();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  const del = async (t) => {
    try { await api.delete('/api/drift-monitoring/tolerances', { params: { scope_type: t.scope_type, scope_value: t.scope_value } }); load(); onChanged && onChanged(); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  return (
    <div className="dm-modal-overlay" onClick={onClose}>
      <div className="dm-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(680px, 94vw)' }}>
        <div className="dm-modal-head"><h3>Tolerance overrides <span className="dm-muted">· fund → SAC → default</span></h3>
          <button className="dm-btn-link" onClick={onClose}>✕</button></div>
        <div style={{ padding: '10px 14px' }}>
          {err && <div className="dm-alert">{err}</div>}
          <div className="dm-daterange" style={{ flexWrap: 'wrap' }}>
            <select value={form.scope_type} onChange={(e) => setForm({ ...form, scope_type: e.target.value })}>
              <option value="default">Default (all)</option>
              <option value="sac">Sub-asset-class</option>
              <option value="fund">Fund (APIR)</option>
            </select>
            {form.scope_type !== 'default' && (
              <input placeholder={form.scope_type === 'sac' ? 'e.g. EQ_AU' : 'APIR e.g. VAN0020AU'} value={form.scope_value}
                     onChange={(e) => setForm({ ...form, scope_value: e.target.value })} style={{ width: 150 }} />
            )}
            <label>Tolerance <input type="number" step="0.1" min="0" value={form.tolerance_pct}
              onChange={(e) => setForm({ ...form, tolerance_pct: e.target.value })} style={{ width: 70 }} /> %</label>
            <input placeholder="note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} style={{ width: 160 }} />
            <button className="dm-btn" onClick={save} disabled={form.scope_type !== 'default' && !form.scope_value}>Save</button>
          </div>
          <div className="dm-table-scroll dm-mini" style={{ marginTop: 8 }}>
            <table className="dm-table"><thead><tr><th>Scope</th><th>Value</th><th className="dm-r">Tol %</th><th>Note</th><th></th></tr></thead>
              <tbody>{rows.map((t) => (
                <tr key={t.scope_type + t.scope_value}>
                  <td>{t.scope_type}</td><td className="dm-code-inline">{t.scope_value || '(all)'}</td>
                  <td className="dm-r">{t.tolerance_pct}</td><td>{t.note || ''}</td>
                  <td><button className="dm-btn-link" onClick={() => del(t)}>remove</button></td>
                </tr>
              ))}{!rows.length && <tr><td colSpan={5} className="dm-muted dm-empty">Only the default request tolerance applies.</td></tr>}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Email-digest configuration tab.
function DigestConfig() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ recipients: '', observed: 'floating', tolerance: 0.5, enabled: false });
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/api/drift-monitoring/digest-config')
      .then((r) => {
        setCfg(r.data);
        setForm({ recipients: r.data.recipients || '', observed: r.data.observed || 'floating',
                  tolerance: r.data.tolerance ?? 0.5, enabled: !!r.data.enabled });
      })
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = cfg && (form.recipients !== (cfg.recipients || '') || form.observed !== cfg.observed
    || Number(form.tolerance) !== cfg.tolerance || form.enabled !== cfg.enabled);
  const recipientCount = (form.recipients || '').split(',').map((x) => x.trim()).filter(Boolean).length;

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.put('/api/drift-monitoring/digest-config',
        { recipients: form.recipients, observed: form.observed, tolerance: Number(form.tolerance), enabled: form.enabled });
      setCfg(r.data); setMsg('Configuration saved.');
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const sendNow = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      if (dirty) await save();
      const r = await api.post('/api/drift-monitoring/digest-run');
      const s = r.data.summary;
      const via = r.data.backend === 'console' ? 'console (no SendGrid key set, not emailed)' : r.data.backend;
      setMsg(`Digest sent to ${r.data.recipients.join(', ')} via ${via}: `
        + `${s.breach_count} drift, ${s.exec_fail_count} exec-fail, ${s.unexpected_count} unexpected.`);
      load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="dm-section">
      <div className="dm-section-head"><h2>Email digest <span className="dm-muted">· daily exception summary</span></h2></div>
      <div style={{ padding: '14px 16px' }}>
        <div className="dm-note" style={{ marginTop: 0 }}>
          This tab manages <strong>who</strong> receives the digest and <strong>what</strong> it contains. The schedule
          (<strong>when</strong>) is still driven externally: a daily task runs <code>python -m scripts.drift_digest</code>,
          which reads this configuration and sends to the recipients below when enabled. With no SendGrid key set, the email
          is printed to the server console instead of sent.
        </div>
        {err && <div className="dm-alert">{err}</div>}
        {msg && <div className="dm-note">{msg}</div>}

        <div className="dm-form">
          <label className="dm-field dm-field-wide">
            <span>Recipients <span className="dm-muted">(comma-separated · {recipientCount})</span></span>
            <input type="text" placeholder="a@drummondcp.com, b@drummondcp.com"
                   value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} />
          </label>
          <label className="dm-field">
            <span>Compare against</span>
            <select value={form.observed} onChange={(e) => setForm({ ...form, observed: e.target.value })}>
              {LEGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="dm-field">
            <span>Tolerance %</span>
            <input type="number" step="0.1" min="0" value={form.tolerance}
                   onChange={(e) => setForm({ ...form, tolerance: e.target.value })} style={{ width: 90 }} />
          </label>
          <label className="dm-field dm-field-check">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            <span>Enabled <span className="dm-muted">(the scheduled task sends only when ticked)</span></span>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' }}>
          <button className="dm-btn" onClick={save} disabled={busy || !dirty}>Save</button>
          <button className="dm-btn dm-btn-ghost" onClick={sendNow} disabled={busy || !recipientCount}
                  title={recipientCount ? 'Build and send the digest now' : 'Add at least one recipient first'}>
            Send now
          </button>
          {busy && <span className="dm-muted">working…</span>}
        </div>

        {cfg && (
          <div className="dm-digest-status">
            <div><span className="dm-muted">Last sent:</span> {cfg.last_sent_at
              ? `${cfg.last_sent_at.slice(0, 16).replace('T', ' ')} · ${cfg.last_sent_summary || ''}`
                + (cfg.last_sent_by ? ` · by ${cfg.last_sent_by}` : '')
              : 'never'}</div>
            <div><span className="dm-muted">Config updated:</span> {cfg.updated_at
              ? `${cfg.updated_at.slice(0, 16).replace('T', ' ')}` + (cfg.updated_by ? ` · by ${cfg.updated_by}` : '')
              : 'not yet saved'}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── In-app guide.
function GuideModal({ onClose }) {
  return (
    <div className="dm-modal-overlay" onClick={onClose}>
      <div className="dm-modal dm-guide" onClick={(e) => e.stopPropagation()}>
        <div className="dm-modal-head">
          <h3>Drift Monitoring &amp; Post-Trade Compliance: guide</h3>
          <button className="dm-btn-link" onClick={onClose}>✕</button>
        </div>
        <div className="dm-guide-body">
          <h4>What it does</h4>
          <p>It answers one question across every managed portfolio: <em>is each vehicle actually holding what the
            approved trading model says it should, and did our trades execute?</em> For every fund it compares the
            <strong> approved trading target</strong> (the maker-checked model weight from the Trading app) against what
            the platform is actually holding (from Shadow_Data), and flags anything materially off.</p>

          <h4>The two sides being compared</h4>
          <ul>
            <li><strong>Approved target</strong>: the operative approved model target per fund per vehicle
              (<code>trading.approved_targets</code>), keyed on Portfolio_Code + APIR.</li>
            <li><strong>Observed</strong>: a Shadow_Data weight at the portfolio's latest reporting date. You choose the
              leg in <em>Compare against</em>: <strong>Floating Target %</strong> (default, best coverage),
              <strong> Last Updated Target %</strong>, or <strong>Shadow Client Portfolio %</strong> (true holdings, but
              sparse). Drift = observed − approved.</li>
          </ul>

          <h4>Tolerance</h4>
          <p>The headline tolerance (default 0.5%) is the band beyond which a difference becomes an exception. You can set
            <strong> per-sub-asset-class or per-fund overrides</strong> via the <em>Tolerances</em> button (fund override
            beats SAC override beats the default): useful because private assets and geared funds legitimately drift more
            than index equities.</p>

          <h4>Status types</h4>
          <ul>
            <li><span className="dm-badge dm-b-ok">OK</span> within tolerance.</li>
            <li><span className="dm-badge dm-b-drift">Large drift</span> held in both, |drift| &gt; tolerance.</li>
            <li><span className="dm-badge dm-b-exec">Execution fail</span> approved above tolerance but not held: a trade
              that didn't happen.</li>
            <li><span className="dm-badge dm-b-unexp">Unexpected</span> held above tolerance but not in the model: a
              holding that shouldn't be there, or wasn't sold.</li>
            <li><span className="dm-badge dm-b-nodata">No data</span> the platform doesn't report the selected leg for that
              fund, so it can't be assessed.</li>
          </ul>

          <h4>Reconciliations (why the numbers aren't noisy)</h4>
          <ul>
            <li><strong>Direct equity</strong>: platforms report individual AU stocks that our pipeline aggregates into one
              "Drummond Direct Equity" line. The model holds that exposure via funds, so EQ_AU is reconciled at the
              sub-asset-class level, not per-fund, avoiding paired false Execution-fail + Unexpected.</li>
            <li><strong>Cash</strong>: operational/settlement cash (labelled inconsistently across platforms) is aligned to
              the model's Platform Cash slot. Deliberate cash <em>funds</em> (e.g. Vanguard Cash Reserve) still show as their
              own drift lines.</li>
          </ul>

          <h4>Staleness</h4>
          <p>A portfolio is flagged <strong>STALE</strong> when its latest Shadow date is either <em>older than its most
            recent approved-target date</em> (the model was changed but no Shadow yet reflects it, so the drift is measured
            against pre-trade holdings) or simply <em>more than two weeks old</em> (the platform has stopped sending fresh
            files). "As at" shows the last Shadow date; treat drift on a stale portfolio with caution.</p>

          <h4>The tabs</h4>
          <ul>
            <li><strong>Exceptions</strong>: every non-OK line, most severe first, with how long it's been in breach.
              Click a portfolio name to open it in the Portfolios tab; use <em>waive</em> to accept a known deviation (it moves
              to "Waived / accepted" and leaves the counts). The summary tiles double as filters.</li>
            <li><strong>Portfolios</strong>: expand a vehicle to see its sub-asset-class → fund drill-down, its net tilt per
              asset-class group (the macro over/underweight), and a link to its drift-over-time.</li>
            <li>Use the <em>Suite</em> dropdown on the Portfolios tab to see only one suite's portfolios (listed in
              Cascade order with portfolio and exception counts); the choice is remembered in this browser. Portfolios
              whose master isn't in the Cascade are under "Unmapped / not in cascade".</li>
            <li><strong>Master matrix</strong>: funds (rows) × the vehicles sharing a master (columns), so a fund that
              drifted in one vehicle but not its siblings stands out. Growth / Defensive totals sit at the foot,
              using each sub asset class's growth exposure from Investment Config. <strong>Export Excel</strong> includes
              every master's matrix (observed and drift sheets) with portfolio names, codes and APIRs.</li>
            <li><strong>Platform / data quality</strong>: reporting coverage and staleness rolled up by platform (for
              chasing platforms that lag), plus guardrails (approved sets not footing to 100%, model funds with no APIR).</li>
            <li><strong>Unassessed</strong>: vehicles with an approved model but no Shadow holdings ("targets only, can't
              verify"), and vehicles with no approved model.</li>
            <li><strong>Drift over time</strong>: gross drift (Σ|floating − target|) per week for a master (its actual master
              portfolio by default, or the average across vehicles) or a single vehicle. Drag the brush or use the date
              pickers to select a period; click a point for the per-fund diff on that exact date.</li>
          </ul>

          <h4>Where the target history comes from</h4>
          <p>The drift-over-time target is a forward-filled daily series stored as change-points. From the trading app's
            approved model versions from mid-2026 onward; before that it's <strong>preseeded from Shadow's Last_Updated
            target</strong> (back to 2019), which is the best record of what the target was historically.</p>

          <h4>Ordering &amp; scope</h4>
          <p>Portfolios are ordered like the Trading app's Cascade: suites and flagship masters (Strategic Series) first.
            Only <strong>active</strong> portfolios appear (from the investment-config portfolio mapping); mark a dead vehicle
            inactive there to hide it.</p>

          <h4>Daily digest</h4>
          <p>The <strong>Email digest</strong> tab configures who receives the daily exception summary and what it contains
            (recipients, leg, tolerance, on/off), and has a <em>Send now</em> button. A scheduled task emails the day's
            exceptions with a new-vs-resolved diff since the previous run, so this works as a monitoring control rather than
            only on demand. The schedule itself is external; the tab manages the recipients and content.</p>

          <p className="dm-muted" style={{ marginTop: 12 }}>Source: approved trading targets vs Shadow_Data holdings. No
            fallback data: missing inputs surface as errors.</p>
        </div>
      </div>
    </div>
  );
}

export default function DriftMonitoringApp() {
  const [observed, setObserved] = useState('floating');
  const [tolerance, setTolerance] = useState(0.5);
  const [platform, setPlatform] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [staleOnly, setStaleOnly] = useState(false);
  const [tab, setTab] = useState('exceptions');
  const [expanded, setExpanded] = useState({});
  const [driftScope, setDriftScope] = useState(null);   // click-through into Drift-over-time
  const [tolOpen, setTolOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api.get('/api/drift-monitoring/report', { params: { observed, tolerance, platform: platform || undefined } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [observed, tolerance, platform]);

  useEffect(() => { load(); }, [load]);

  // Suite filter for the Portfolios tab. The chosen suite is remembered per browser
  // (a convenience, not shared state) so whoever owns a suite lands straight on it.
  const [suites, setSuites] = useState(null);             // { suiteOf: {blender_code: suite}, order: [suite…] }
  const [suitesErr, setSuitesErr] = useState(null);
  const [suite, setSuiteState] = useState(() => {
    try { return localStorage.getItem(SUITE_KEY) || ''; } catch { return ''; }
  });
  const setSuite = (s) => {
    setSuiteState(s);
    try { if (s) localStorage.setItem(SUITE_KEY, s); else localStorage.removeItem(SUITE_KEY); } catch { /* storage blocked */ }
  };
  useEffect(() => {
    api.get('/api/drift-monitoring/masters')
      .then((r) => {
        const suiteOf = {}; const order = [];
        (r.data.masters || []).forEach((m) => {                // masters arrive cascade-ordered
          suiteOf[m.blender_code] = m.suite;
          if (!order.includes(m.suite)) order.push(m.suite);
        });
        setSuites({ suiteOf, order });
      })
      .catch((e) => setSuitesErr(e.response?.data?.detail || e.message));
  }, []);

  const goToDrift = (portfolio_code, blender_code) => {
    setDriftScope({ bc: blender_code || '', pc: portfolio_code });
    setTab('targets');
  };
  const goToPortfolio = (portfolio_code) => {
    setStaleOnly(false);                                   // don't let the filters hide it
    const p = (data?.portfolios || []).find((x) => x.portfolio_code === portfolio_code);
    if (suite && suites && p && (suites.suiteOf[p.blender_code] || NO_SUITE) !== suite)
      setSuiteState('');                                   // show all this visit; the saved suite stays remembered
    setExpanded((x) => ({ ...x, [portfolio_code]: true }));
    setTab('portfolios');
    setTimeout(() => {                                     // scroll the row into view once rendered
      const el = document.getElementById(`dm-pf-${portfolio_code}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  };
  const waive = async (e) => {
    const reason = window.prompt(`Accept/waive drift for ${e.fund_name} in ${e.portfolio_name}? Reason:`);
    if (reason == null) return;
    try {
      await api.post('/api/drift-monitoring/waivers', { portfolio_code: e.portfolio_code, apir_code: e.apir_code, reason });
      load();
    } catch (err) { setError('Waive failed: ' + (err.response?.data?.detail || err.message)); }
  };
  const unwaive = async (e) => {
    try {
      await api.delete('/api/drift-monitoring/waivers', { params: { portfolio_code: e.portfolio_code, apir_code: e.apir_code } });
      load();
    } catch (err) { setError('Un-waive failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const download = async (kind) => {
    try {
      const r = await api.get(`/api/drift-monitoring/report.${kind}`,
        { params: { observed, tolerance, platform: platform || undefined }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `drift_monitoring.${kind}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(`${kind.toUpperCase()} export failed: ` + (e.response?.data?.detail || e.message)); }
  };

  const platforms = useMemo(() => {
    if (!data) return [];
    const all = [...(data.portfolios || []), ...(data.not_reported || []), ...(data.no_model || [])];
    return [...new Set(all.map((p) => p.platform).filter(Boolean))].sort();
  }, [data]);

  const exceptions = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return (data.exceptions || []).filter((e) => {
      if (statusFilter && e.status !== statusFilter) return false;
      if (!q) return true;
      return (e.portfolio_name || '').toLowerCase().includes(q) || (e.fund_name || '').toLowerCase().includes(q)
        || (e.apir_code || '').toLowerCase().includes(q);
    });
  }, [data, statusFilter, search]);

  const s = data?.summary || {};
  const toggle = (pc) => setExpanded((x) => ({ ...x, [pc]: !x[pc] }));

  return (
    <div className="dm-wrap">
      <div className="dm-head">
        <h1>Drift Monitoring &amp; Post-Trade Compliance
          <button className="dm-guide-btn" onClick={() => setGuideOpen(true)} title="How this app works">Guide</button>
        </h1>
        <p className="dm-muted">Approved trading targets vs Shadow_Data holdings: large drift, execution failures and holdings that shouldn't be there.</p>
      </div>

      <div className="dm-toolbar">
        <label>Compare against
          <select value={observed} onChange={(e) => setObserved(e.target.value)}>
            {LEGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label>Tolerance
          <input type="number" step="0.1" min="0" value={tolerance}
                 onChange={(e) => setTolerance(parseFloat(e.target.value) || 0)} style={{ width: 64 }} /> %
        </label>
        <label>Platform
          <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="">All</option>
            {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <button className="dm-btn" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        <button className="dm-btn dm-btn-ghost" onClick={() => setTolOpen(true)}>Tolerances</button>
        <button className="dm-btn dm-btn-ghost" onClick={() => download('png')}>Download PNG</button>
        <button className="dm-btn dm-btn-ghost" onClick={() => download('xlsx')}>Export Excel</button>
        {data && <span className="dm-muted">Observed: {data.observed_label} · tol {data.tolerance}% · {String(data.generated_at).slice(0, 16).replace('T', ' ')}</span>}
      </div>
      {error && <div className="dm-alert">{error}</div>}

      {data && (
        <>
          <div className="dm-tiles">
            {TILES.map(([key, label, cls, sf]) => (
              <button key={key} className={`dm-tile ${cls} ${statusFilter === sf && sf ? 'dm-tile-on' : ''}`}
                      onClick={() => { if (sf) { setStatusFilter(statusFilter === sf ? null : sf); setTab('exceptions'); } }}
                      disabled={!sf} title={sf ? 'Filter exceptions' : ''}>
                <div className="dm-tile-n">{s[key] ?? 0}</div>
                <div className="dm-tile-l">{label}</div>
              </button>
            ))}
          </div>

          <div className="dm-tabs">
            {[
              ['exceptions', 'Exceptions', exceptions.length],
              ['portfolios', 'Portfolios', data.portfolios.length],
              ['matrix', 'Master matrix', null],
              ['platform', 'Platform / data quality', (data.data_quality || []).length || null],
              ['unassessed', 'Unassessed', data.not_reported.length + data.no_model.length],
              ['targets', 'Drift over time', null],
              ['digest', 'Email digest', null],
            ].map(([k, label, count]) => (
              <button key={k} className={`dm-tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
                {label}{count != null && <span className="dm-tab-n">{count}</span>}
              </button>
            ))}
          </div>

          {tab === 'exceptions' && (
          <div className="dm-section">
            <div className="dm-section-head">
              <h2>Exceptions {statusFilter && <span className="dm-muted">· {STATUS_META[statusFilter][0]} only</span>}</h2>
              <div className="dm-filters">
                {statusFilter && <button className="dm-btn-link" onClick={() => setStatusFilter(null)}>clear filter</button>}
                <input placeholder="Search portfolio / fund / APIR…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="dm-table-scroll">
              <table className="dm-table">
                <thead><tr>
                  <th>Portfolio</th><th>Fund</th><th>Sub Asset Class</th>
                  <th className="dm-r">Approved</th><th className="dm-r">Floating</th>
                  <th className="dm-r">Observed</th><th className="dm-r">Drift</th>
                  <th className="dm-r">In breach</th><th>Status</th><th></th>
                </tr></thead>
                <tbody>
                  {exceptions.slice(0, 500).map((e, i) => (
                    <tr key={i} className={`dm-row-${(e.severity || 'na')}`}>
                      <td>
                        <button className="dm-link-cell" title="View in Portfolios tab"
                                onClick={() => goToPortfolio(e.portfolio_code)}>{e.portfolio_name}</button>
                        <span className="dm-code-inline">{e.portfolio_code}</span>
                      </td>
                      <td>{e.fund_name}</td><td>{e.sub_asset_class}</td>
                      <td className="dm-r">{fmt(e.approved_target)}</td>
                      <td className="dm-r">{fmt(e.floating)}</td>
                      <td className="dm-r">{fmt(e.observed)}</td>
                      <td className="dm-r dm-drift">{fmt(e.drift, true)}</td>
                      <td className="dm-r" title={e.in_breach_since ? `since ${e.in_breach_since}` : ''}>
                        {e.weeks_in_breach ? `${e.weeks_in_breach}w` : ''}</td>
                      <td><Badge status={e.status} /></td>
                      <td>{e.apir_code && <button className="dm-btn-link" onClick={() => waive(e)}>waive</button>}</td>
                    </tr>
                  ))}
                  {!exceptions.length && <tr><td colSpan={10} className="dm-muted dm-empty">No exceptions for this filter.</td></tr>}
                </tbody>
              </table>
              {exceptions.length > 500 && <div className="dm-muted dm-empty">Showing first 500 of {exceptions.length}. Narrow with a filter or export to Excel for the full list.</div>}
            </div>
            {(data.waived || []).length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div className="dm-section-head" style={{ borderRadius: 6 }}>
                  <h2>Waived / accepted <span className="dm-muted">· {data.waived.length} · excluded from the counts above</span></h2>
                </div>
                <div className="dm-table-scroll dm-mini">
                  <table className="dm-table"><thead><tr>
                    <th>Portfolio</th><th>Fund</th><th className="dm-r">Drift</th><th>Status</th><th>Reason</th><th></th>
                  </tr></thead>
                    <tbody>{data.waived.map((e, i) => (
                      <tr key={i}>
                        <td>{e.portfolio_name}<span className="dm-code-inline">{e.portfolio_code}</span></td>
                        <td>{e.fund_name}</td><td className="dm-r dm-drift">{fmt(e.drift, true)}</td>
                        <td><Badge status={e.status} /></td><td>{e.waive_reason || ''}</td>
                        <td><button className="dm-btn-link" onClick={() => unwaive(e)}>un-waive</button></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          )}

          {tab === 'portfolios' && (
            <PortfoliosView data={data} suiteOf={suites?.suiteOf} suiteOrder={suites?.order} suitesErr={suitesErr}
                            suite={suite} setSuite={setSuite} staleOnly={staleOnly} setStaleOnly={setStaleOnly}
                            expanded={expanded} onToggle={toggle} onDrift={goToDrift} />
          )}

          {tab === 'matrix' && <MasterMatrix data={data} />}

          {tab === 'platform' && <PlatformView data={data} />}

          {tab === 'unassessed' && (
          <div className="dm-grid2">
            <div className="dm-section">
              <div className="dm-section-head"><h2>Approved model, no holdings reported <span className="dm-muted">· {data.not_reported.length}</span></h2></div>
              <p className="dm-muted dm-pad">These vehicles have an approved trading model but no Shadow_Data holdings. The platform isn't reporting positions, so execution can't be assessed (not counted as failures).</p>
              <div className="dm-table-scroll dm-mini">
                <table className="dm-table"><thead><tr><th>Portfolio</th><th>Code</th><th>Platform</th><th>Master</th><th>Last Shadow</th><th className="dm-r">Approved funds</th></tr></thead>
                  <tbody>{data.not_reported.map((p) => (
                    <tr key={p.portfolio_code}><td>{p.portfolio_name}</td><td className="dm-code-inline">{p.portfolio_code}</td><td>{p.platform || ''}</td><td>{p.blender_code || ''}</td><td>{p.last_shadow_date || 'never'}</td><td className="dm-r">{p.approved_fund_count}</td></tr>
                  ))}{!data.not_reported.length && <tr><td colSpan={6} className="dm-muted dm-empty">None.</td></tr>}</tbody>
                </table>
              </div>
            </div>
            <div className="dm-section">
              <div className="dm-section-head"><h2>No approved model <span className="dm-muted">· {data.no_model.length}</span></h2></div>
              <p className="dm-muted dm-pad">Managed vehicles with no approved trading target set, so there is nothing to compare against.</p>
              <div className="dm-table-scroll dm-mini">
                <table className="dm-table"><thead><tr><th>Portfolio</th><th>Code</th><th>Platform</th><th>Master</th><th>Last Shadow</th><th className="dm-r">Held funds</th></tr></thead>
                  <tbody>{data.no_model.map((p) => (
                    <tr key={p.portfolio_code}><td>{p.portfolio_name}</td><td className="dm-code-inline">{p.portfolio_code}</td><td>{p.platform || ''}</td><td>{p.blender_code || ''}</td><td>{p.last_shadow_date || 'never'}</td><td className="dm-r">{p.held_fund_count}</td></tr>
                  ))}{!data.no_model.length && <tr><td colSpan={6} className="dm-muted dm-empty">None.</td></tr>}</tbody>
                </table>
              </div>
            </div>
          </div>
          )}

          {tab === 'targets' && <DriftOverTime scope={driftScope} />}

          {tab === 'digest' && <DigestConfig />}
        </>
      )}
      {tolOpen && <TolerancesModal onClose={() => setTolOpen(false)} onChanged={load} />}
      {guideOpen && <GuideModal onClose={() => setGuideOpen(false)} />}
    </div>
  );
}
